import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  createFileRoute,
  Link,
  useNavigate,
  type ErrorComponentProps,
} from '@tanstack/react-router'
import { MoreVertical, Plus } from 'lucide-react'
import { createContext, useContext, useId, useState, type MouseEvent } from 'react'

import alertMitraArt from '@/assets/mitra-3d/alerts-empty.png'
import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import Button from '@/components/ui/Button'
import Change from '@/components/ui/Change'
import DataTable, { type DataTableColumn } from '@/components/ui/DataTable'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import IconButton from '@/components/ui/IconButton'
import { MenuActionItem, ROW as MENU_ROW } from '@/components/ui/MenuItem'
import Modal from '@/components/ui/Modal'
import Money from '@/components/ui/Money'
import Popover from '@/components/ui/Popover'
import QueryState from '@/components/ui/QueryState'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import Skeleton from '@/components/ui/Skeleton'
import SymbolCell from '@/components/ui/SymbolCell'
import TitleRow from '@/components/ui/TitleRow'
import { PRESS_CARD } from '@/components/ui/press'
import SegmentedControl, { type SegmentOption } from '@/components/ui/SegmentedControl'
import { toast } from '@/components/ui/toast-store'
import RefreshRegion from '@/components/ui/RefreshRegion'
import UpdatingNote from '@/components/ui/UpdatingNote'
import { alertErrorMessage } from '@/features/alerts/api/errors'
import {
  useRearmAlert,
  useRemoveAlert,
  useSetAlertActive,
  useUpdateAlert,
} from '@/features/alerts/api/mutations'
import { alertsQueryOptions } from '@/features/alerts/api/queries'
import AlertDialogArt from '@/features/alerts/components/AlertDialogArt'
import AlertForm from '@/features/alerts/components/AlertForm'
import NewAlertModal from '@/features/alerts/components/NewAlertModal'
import { formatAlertExpiry } from '@/features/alerts/lib/expiry'
import { formatFiredAt } from '@/features/alerts/lib/firedAt'
import { formatAlertTrigger } from '@/features/alerts/lib/level'
import UpgradeRequired from '@/features/subscriptions/components/UpgradeRequired'
import { upgradeToast } from '@/features/subscriptions/lib/upgradeToast'
import { subscriptionRequiredFeature } from '@/features/subscriptions/lib/paywall'
import { useWatchlistCollection } from '@/features/watchlist/api/queries'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import { formatQuantity } from '@/lib/format'
import { cn } from '@/lib/utils'
import { alertTitle, type Alert, type AlertTab } from '@/schemas/alerts'
import { alertsSearchSchema, loadAlerts } from '@/screens/app/AlertsScreen.loader'

/** The title row every state of the page shares. */
const HEADER = <TitleRow title="Alerts" />

/**
 * The loaded page's frame at 1440 (the 728px column): the title row, a bar
 * where the 36px "New alert" pill goes (it opens a modal the screen owns, so
 * the pending page cannot offer it) and the table — a typical three rows, as
 * the row count is the member's.
 */
function AlertsPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading your alerts…"
      header={HEADER}
      actions={<Skeleton className="h-9 w-32 rounded-md" />}
      pageTitle="Alerts"
    >
      <AlertsTableSkeleton />
    </ScreenLayout>
  )
}

/** The copy this page gave a failed first load before it had a loader. */
function AlertsError(props: ErrorComponentProps) {
  // A gated read refused under enforce (CONTRACT §7) is not a failed load.
  const feature = subscriptionRequiredFeature(props.error)
  if (feature !== null) {
    return (
      <ScreenLayout header={HEADER} pageTitle="Alerts">
        <UpgradeRequired featureKey={feature} />
      </ScreenLayout>
    )
  }
  return (
    <RouteErrorPage
      {...props}
      header={HEADER}
      title="Alerts did not load"
      description="Check your connection and try again."
    />
  )
}

export const Route = createFileRoute('/_app/alerts')({
  validateSearch: alertsSearchSchema,
  loader: loadAlerts,
  pendingComponent: AlertsPending,
  errorComponent: AlertsError,
  component: AlertsScreen,
})

/**
 * The Status column's cell — V2 (`4236:109443`) draws a dot plus a word, not
 * the `Badge` pill this screen used before: a muted "Waiting" dot for an
 * armed alert, and — the one populated state a render actually shows —
 * "Fired 2:41 PM" in a brighter weight with the same gold dot `warning-fill`
 * already means elsewhere (`ImportSummaryCard`). EXPIRED reads "Expired 30
 * Bhadra 2083" (the mock's Expired tab, with the year because an expired alert
 * may be older than this one), PAUSED "Paused"; both in the same quiet
 * "Waiting" treatment, the least invented reading of states the design draws
 * only in passing.
 */
function AlertStatusCell({ alert }: { alert: Alert }) {
  if (alert.status === 'FIRED' && alert.firedAt !== null) {
    return (
      <span className="flex items-center justify-end gap-2">
        <span
          aria-hidden="true"
          className="size-2 shrink-0 animate-dot-pulse rounded-full bg-warning-fill"
        />
        {/* A fire older than today prints the full "12 Bhadra 2083, 3:00 PM"
            (`formatFiredAt`), which wrapped inside this column's old fixed
            166px — `whitespace-nowrap` plus the column's own `fixedWidth`
            (below) let it grow instead. */}
        <span className="text-body-xs whitespace-nowrap text-foreground">
          Fired {formatFiredAt(alert.firedAt)}
        </span>
      </span>
    )
  }
  const label =
    alert.status === 'EXPIRED'
      ? `Expired ${formatAlertExpiry(alert.expiresOn)}`
      : alert.status === 'PAUSED'
        ? 'Paused'
        : 'Waiting'
  return (
    <span className="flex items-center justify-end gap-2">
      <span
        aria-hidden="true"
        className="size-2 shrink-0 rounded-full bg-muted-foreground-subtle"
      />
      <span className="text-body-xs whitespace-nowrap text-muted-foreground">{label}</span>
    </span>
  )
}

/** The mock's menu rows are 14, not the shared 12. */
const ITEM = 'text-label-l'

/**
 * The row's own "⋮" — V2 (`4236:109443`) never opens one in any captured
 * render, so its contents are inferred from what a row can actually do.
 * "Edit"/"Delete" are the same two the old card-grid's kebab already offered.
 * "Open <symbol>"/"Set again" are new here: the OLD design's fired-alert
 * two-button footer has nowhere to live in a single table row, so both moves
 * into this menu for a fired alert specifically, rather than being dropped.
 * "Pause alert"/"Resume alert" are the experiment draft's row action
 * (`PATCH active`): one for an alert that is waiting, the other for a paused one.
 */
function AlertRowMenu({
  alert,
  onEdit,
  onDelete,
  onSetAgain,
  onSetItAgain,
  onTogglePause,
  setAgainPending,
  togglePending,
}: {
  alert: Alert
  onEdit: () => void
  onDelete: () => void
  onSetAgain: () => void
  /** An expired alert is set again as a NEW alert pre-filled from it (`POST /alerts/`). */
  onSetItAgain: () => void
  onTogglePause: () => void
  setAgainPending: boolean
  togglePending: boolean
}) {
  const [open, setOpen] = useState(false)
  const fired = alert.status === 'FIRED'
  const expired = alert.status === 'EXPIRED'
  const paused = alert.status === 'PAUSED'
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      hasPopup="menu"
      label="Alert actions"
      // 200 wide on the mock's menu shadow (`0 14 32`) over a hairline edge.
      className="w-50 border-border-subtle shadow-(--shadow-select-menu)"
      trigger={(triggerProps) => (
        // Mock `wl-rowmenu`: a quiet 36 square (#737370 → our tertiary) that lights to white on the
        // inset grey on hover, focus, and while its menu is open.
        <IconButton
          {...triggerProps}
          aria-label="Alert actions"
          className="size-9 shrink-0 rounded-sm text-muted-foreground-subtle hover:bg-muted hover:text-foreground focus-visible:bg-muted focus-visible:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground"
        >
          <MoreVertical className="size-4" aria-hidden="true" />
        </IconButton>
      )}
    >
      {expired ? (
        <MenuActionItem
          label="Set it again"
          className={ITEM}
          tabIndex={-1}
          onClick={() => {
            setOpen(false)
            onSetItAgain()
          }}
        />
      ) : (
        <>
          <MenuActionItem
            label="Edit alert"
            className={ITEM}
            tabIndex={-1}
            onClick={() => {
              setOpen(false)
              onEdit()
            }}
          />
          <Link
            to="/stocks/$symbol"
            params={{ symbol: alert.symbol }}
            role="menuitem"
            tabIndex={-1}
            onClick={() => {
              setOpen(false)
            }}
            className={cn(
              MENU_ROW,
              ITEM,
              'cursor-pointer gap-2 text-popover-foreground hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
            )}
          >
            Open {alert.symbol}
          </Link>
        </>
      )}
      {fired ? (
        <MenuActionItem
          label="Set again"
          className={ITEM}
          disabled={setAgainPending}
          tabIndex={-1}
          onClick={() => {
            setOpen(false)
            onSetAgain()
          }}
        />
      ) : null}
      {paused || alert.status === 'ACTIVE' ? (
        <MenuActionItem
          label={paused ? 'Resume alert' : 'Pause alert'}
          className={ITEM}
          disabled={togglePending}
          tabIndex={-1}
          onClick={() => {
            setOpen(false)
            onTogglePause()
          }}
        />
      ) : null}
      <MenuActionItem
        label="Delete alert"
        className={ITEM}
        danger
        tabIndex={-1}
        onClick={() => {
          setOpen(false)
          onDelete()
        }}
      />
    </Popover>
  )
}

/**
 * What a row's menu does, which is the screen's to decide: its dialogs and the set-again
 * mutation. Through context, so `COLUMNS` stays module-level and a re-render (a refetch, a
 * mutation's progress) never remounts the cells, which closed a menu that was open (see
 * DataTable's `columns`).
 */
interface RowActions {
  edit: (alert: Alert) => void
  remove: (alert: Alert) => void
  setAgain: (alert: Alert) => void
  /** An expired alert, set again as a new alert (the form opens pre-filled). */
  setItAgain: (alert: Alert) => void
  /** Pause a waiting alert, resume a paused one. */
  togglePause: (alert: Alert) => void
  /** The alert whose "Set again" is in flight, if any. */
  settingAgain: Alert['id'] | undefined
  /** The alert whose pause or resume is in flight, if any. */
  pausing: Alert['id'] | undefined
}

const RowActionsContext = createContext<RowActions | null>(null)

function AlertMenuCell({ alert }: { alert: Alert }) {
  const actions = useContext(RowActionsContext)
  if (actions === null) return null
  return (
    <AlertRowMenu
      alert={alert}
      onEdit={() => {
        actions.edit(alert)
      }}
      onDelete={() => {
        actions.remove(alert)
      }}
      setAgainPending={actions.settingAgain === alert.id}
      onSetAgain={() => {
        actions.setAgain(alert)
      }}
      onSetItAgain={() => {
        actions.setItAgain(alert)
      }}
      togglePending={actions.pausing === alert.id}
      onTogglePause={() => {
        actions.togglePause(alert)
      }}
    />
  )
}

/** What the Last traded column sorts and reads by: the reading the alert's own kind watches. */
const lastReading = (alert: Alert): number => {
  switch (alert.kind) {
    case 'volume':
      return alert.lastVolume ?? 0
    case 'change':
      return Number(alert.lastChangePercent ?? 0)
    default:
      return Number(alert.lastTraded ?? 0)
  }
}

const COLUMNS: DataTableColumn<Alert>[] = [
  {
    id: 'alert',
    accessorFn: (alert) => alertTitle(alert),
    header: 'Alert',
    enableSorting: false,
    // 4236:109443: 220px fixed, a mark on every row.
    meta: { fixedWidth: { base: 220, min: 160 } },
    cell: ({ row }) => {
      const alert = row.original
      return (
        <SymbolCell
          symbol={alertTitle(alert)}
          markSymbol={alert.symbol}
          name={alert.company}
          reserveMark
          look="alert"
        />
      )
    },
  },
  {
    // Left unwidthed on purpose — the design's 120px is tight for a longer level
    // ("Below 12,450.00") and this is the one column `fixedWidth`'s own rule requires: at
    // least one plain, unwidthed sibling to absorb the slack the table would otherwise
    // hand to every OTHER column, `alert`'s clamp included (see DataTable.tsx's
    // `fixedWidth` doc).
    id: 'trigger',
    accessorFn: (alert) => Number(alert.trigger),
    header: 'Trigger',
    enableSorting: false,
    // Mock: 14/400, right-aligned (the table's body size sets the 14).
    meta: { numeric: true },
    cell: ({ row }) => {
      const alert = row.original
      return <span className="tnum">{formatAlertTrigger(alert, 'page')}</span>
    },
  },
  {
    id: 'lastTraded',
    accessorFn: (alert) => lastReading(alert),
    // Not "Last traded (Rs)": a volume alert's row is a share count, not
    // rupees, and the old header called it that regardless — a wrong
    // label, not just a missing unit. Each row states its own unit now:
    // `currency` on the price cell's `Money`, "kitta" on the volume one,
    // the same suffix `Trigger`'s own volume cell already uses.
    header: 'Last traded',
    enableSorting: false,
    meta: { width: '100px', numeric: true },
    cell: ({ row }) => {
      const alert = row.original
      // Volume watches a share count and a % move watches today's move; every other kind, RSI
      // and MA included, shows the LTP: the row carries no RSI reading.
      if (alert.kind === 'change') return <Change value={alert.lastChangePercent} percent />
      if (alert.kind !== 'volume') return <Money value={alert.lastTraded} currency />
      return (
        <span className="tnum">
          {alert.lastVolume === null ? '—' : `${formatQuantity(alert.lastVolume)} kitta`}
        </span>
      )
    },
  },
  {
    id: 'status',
    accessorFn: (alert) => alert.status,
    header: 'Status',
    enableSorting: false,
    // A fixed `166px` (off the 4-grid) wrapped "Fired 12 Bhadra 2083, 3:00
    // PM" onto two lines at the 844px main column [wao] — `fixedWidth`
    // grows the column toward `base` when the table has room instead.
    meta: { fixedWidth: { base: 208, min: 120 }, align: 'right' },
    cell: ({ row }) => <AlertStatusCell alert={row.original} />,
  },
  {
    id: 'menu',
    accessorFn: (alert) => alert.id,
    header: '',
    enableSorting: false,
    meta: { width: '56px', align: 'right' },
    cell: ({ row }) => <AlertMenuCell alert={row.original} />,
  },
]

/**
 * A content-shaped stand-in for the table while its first page loads: the
 * shared table's own loading shape, three two-line rows, the typical count.
 */
function AlertsTableSkeleton() {
  return <DataTableSkeleton rows={3} columns={4} rowHeight="lg" />
}

const TAB_OPTIONS: readonly SegmentOption<AlertTab>[] = [
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
]

/**
 * Phone (A3, mock `<lg` grid card): one card per alert — name · status · ⋮ over "Below 480.00 …
 * LTP 498.00" — in place of the table, whose Status and ⋮ ran off the screen. The body is a real
 * button that does what the row does (Edit, or set an expired one again); the ⋮ sits beside it.
 */
function AlertCards({
  rows,
  openRow,
}: {
  rows: readonly Alert[]
  openRow: (alert: Alert) => (event: MouseEvent<HTMLElement>) => void
}) {
  const actions = useContext(RowActionsContext)
  return (
    <ul className="flex flex-col gap-3">
      {rows.map((alert) => (
        <li
          key={alert.id}
          className="flex items-start gap-2 rounded-md border border-border-subtle bg-card p-4"
        >
          <button
            type="button"
            onClick={openRow(alert)}
            className={cn(
              'flex min-w-0 flex-1 flex-col gap-3 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring-control',
              PRESS_CARD,
            )}
          >
            <SymbolCell
              symbol={alertTitle(alert)}
              markSymbol={alert.symbol}
              name={alert.company}
              reserveMark
              look="alert"
            />
            <span className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-body-m">
              <span className="tnum">{formatAlertTrigger(alert, 'page')}</span>
              <span className="tnum text-muted-foreground">
                {alert.kind === 'change' ? 'Today ' : 'LTP '}
                {alert.kind === 'change' ? (
                  <Change value={alert.lastChangePercent} percent />
                ) : alert.kind !== 'volume' ? (
                  <Money value={alert.lastTraded} />
                ) : alert.lastVolume === null ? (
                  '—'
                ) : (
                  `${formatQuantity(alert.lastVolume)} kitta`
                )}
              </span>
            </span>
            <AlertStatusCell alert={alert} />
          </button>
          {actions === null ? null : <AlertMenuCell alert={alert} />}
        </li>
      ))}
    </ul>
  )
}

function AlertsScreen() {
  // `tab` is the URL's own `?status=` now — the sidebar's ALERTS group
  // (`alertsGroup()`, `nav-groups.ts`) is the switcher, so this screen only
  // reads it and has no tab state or strip of its own.
  const { symbol, status: tab } = Route.useSearch()
  // Not a suspense read: the tab is a search param, and a suspense query
  // whose key changed would put the page back on its pending skeleton. The
  // loader has cached the tab the page opened on; a switch keeps the previous
  // tab's rows on screen, as they were, until the next tab's land, and a
  // switch that fails is QueryState's to show, in place of the table only.
  const query = useQuery({ ...alertsQueryOptions(tab), placeholderData: keepPreviousData })
  const companies = useWatchlistCollection()
  const updateAlert = useUpdateAlert()
  const removeAlert = useRemoveAlert()
  const rearmAlert = useRearmAlert()
  const setActiveAlert = useSetAlertActive()
  const navigate = useNavigate()
  const desktop = useIsDesktop()
  // New alert now lives in the shared `NewAlertModal` — this screen only
  // decides when it starts open (the `?symbol=` deep link).
  const [newAlertOpen, setNewAlertOpen] = useState(Boolean(symbol))
  // An expired alert being set again: the same dialog, starting from its levels.
  const [reuse, setReuse] = useState<Alert>()
  const [editing, setEditing] = useState<Alert>()
  const [editOpen, setEditOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const alertFormId = useId()
  const busy = updateAlert.isPending || removeAlert.isPending
  const candidates = companies.data?.candidates ?? []
  const paywallFeature = subscriptionRequiredFeature(query.error)

  const newAlert = () => {
    setReuse(undefined)
    setNewAlertOpen(true)
  }

  // Expired rows: a click (or "Set it again") opens New alert pre-filled from the row.
  const setItAgain = (alert: Alert) => {
    setReuse(alert)
    setNewAlertOpen(true)
  }

  // The row is the target (mock): active and fired open Edit, expired sets it again. A click on
  // a control inside the row (⋮, its menu) is that control's own; a menu panel is portalled, so
  // its clicks bubble here through React and are dropped by the containment check.
  const openRow = (alert: Alert) => (event: MouseEvent<HTMLElement>) => {
    if (!(event.target instanceof Element) || !event.currentTarget.contains(event.target)) return
    if (event.target.closest('button, a, [role="menu"]') !== null) return
    if (alert.status === 'EXPIRED') setItAgain(alert)
    else editAlert(alert)
  }

  const editAlert = (alert: Alert) => {
    setEditing(alert)
    setEditOpen(true)
  }

  // Reached straight from a row's "⋮ · Delete" now, not only from inside the
  // editor's own footer — both paths share the same confirm dialog below.
  const confirmDelete = (alert: Alert) => {
    setEditing(alert)
    setConfirmOpen(true)
  }

  // Re-arming and resuming are the plan-gated writes (pausing never is); both say so the same way.
  const reportWriteFailure = (error: unknown) => {
    const feature = subscriptionRequiredFeature(error)
    if (feature === null) {
      toast.error(alertErrorMessage(error))
      return
    }
    upgradeToast(feature, () => {
      void navigate({ to: '/subscription' })
    })
  }

  const setAgain = (alert: Alert) => {
    rearmAlert.mutate(alert.id, { onError: reportWriteFailure })
  }

  const togglePause = (alert: Alert) => {
    setActiveAlert.mutate(
      { id: alert.id, active: alert.status === 'PAUSED' },
      { onError: reportWriteFailure },
    )
  }

  const rowActions: RowActions = {
    edit: editAlert,
    remove: confirmDelete,
    setAgain,
    setItAgain,
    togglePause,
    settingAgain: rearmAlert.isPending ? rearmAlert.variables : undefined,
    pausing: setActiveAlert.isPending ? setActiveAlert.variables.id : undefined,
  }

  return (
    <ScreenLayout
      pageTitle="Alerts"
      header={HEADER}
      // On the section strip's row from `lg` up, beside the title below it.
      actions={
        <>
          {/* Leads the pill, so the note grows leftwards into the free
              space and the button stays put. */}
          <UpdatingNote when={query.isPlaceholderData} label="Updating alerts…" />
          <Button type="button" variant="secondary" aria-haspopup="dialog" onClick={newAlert}>
            <Plus className="mr-2 size-4" aria-hidden="true" /> New alert
          </Button>
        </>
      }
    >
      <NewAlertModal
        open={newAlertOpen}
        onClose={() => {
          setNewAlertOpen(false)
        }}
        {...(symbol ? { presetSymbol: symbol } : {})}
        {...(reuse ? { presetAlert: reuse } : {})}
      />

      {editing ? (
        <Modal
          open={editOpen}
          onOpenChange={setEditOpen}
          size="520"
          icon={<AlertDialogArt />}
          title="Edit alert"
          description="Change the level, the channel or the expiry."
          footer={
            <>
              <Button
                variant="secondary"
                size="lg"
                className="text-destructive"
                disabled={busy}
                onClick={() => {
                  setConfirmOpen(true)
                }}
              >
                Delete alert
              </Button>
              <Button type="submit" size="lg" form={alertFormId} disabled={busy}>
                {updateAlert.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </>
          }
        >
          {/* Unmounted while closed so each open starts from a fresh draft. */}
          {editOpen ? (
            <AlertForm
              key={editing.id}
              id={alertFormId}
              initial={editing}
              candidates={candidates}
              {...(updateAlert.isError ? { error: alertErrorMessage(updateAlert.error) } : {})}
              onSave={(draft) => {
                updateAlert.mutate(
                  { saved: editing, draft },
                  {
                    onSuccess: () => {
                      setEditOpen(false)
                    },
                  },
                )
              }}
            />
          ) : null}
        </Modal>
      ) : null}

      {editing ? (
        <Modal
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          role="alertdialog"
          size="sm"
          title={`Delete ${alertTitle(editing)}?`}
          description="Mitra stops watching this alert. You can set a new alert any time."
          footer={
            <>
              <Button
                variant="secondary"
                size="lg"
                onClick={() => {
                  setConfirmOpen(false)
                }}
              >
                Keep it
              </Button>
              <Button
                variant="destructive"
                size="lg"
                disabled={removeAlert.isPending}
                onClick={() => {
                  removeAlert.mutate(editing.id, {
                    onSuccess: () => {
                      setConfirmOpen(false)
                      setEditOpen(false)
                    },
                  })
                }}
              >
                {removeAlert.isPending ? 'Deleting…' : 'Delete alert'}
              </Button>
            </>
          }
        >
          {removeAlert.isError ? (
            <p role="alert" className="text-body-s text-destructive">
              {alertErrorMessage(removeAlert.error)}
            </p>
          ) : null}
        </Modal>
      ) : null}

      {/* Phone (A3): the strip's Active / Expired as a switch on the page, as the sidebar group
          is off screen below `lg`. */}
      <SegmentedControl
        options={TAB_OPTIONS}
        value={tab}
        onChange={(next) => {
          void navigate({ to: '/alerts', search: (previous) => ({ ...previous, status: next }) })
        }}
        ariaLabel="Alerts"
        size="lg"
        className="mb-4 lg:hidden"
      />

      {paywallFeature !== null ? (
        <UpgradeRequired featureKey={paywallFeature} size="section" framed />
      ) : (
        <QueryState
          query={query}
          isEmpty={(rows) => rows.length === 0}
          skeleton={<AlertsTableSkeleton />}
          loadingLabel="Loading your alerts…"
          size="section"
          framed
          empty={{
            // The mock's sleepy Mitra holding a bell (`mitra-3d/alerts-empty`), one gold "Browse
            // the market" button: "New alert" already sits on the strip's row.
            art: alertMitraArt,
            scale: 'roomy',
            title: tab === 'active' ? 'No alerts set yet' : 'No expired alerts',
            description:
              tab === 'active'
                ? 'Open any company, pick a price, and Mitra rings when it is reached.'
                : 'Alerts that reach their expiry date without ringing show up here.',
            action: (
              <Link to="/market" className={cn(stateActionClass('section'))}>
                Browse the market
              </Link>
            ),
          }}
          // A custom `title` drops EmptyState's own default description too
          // (it was written to follow the DEFAULT title) — restated here so
          // the failed card still says something rather than just its heading.
          failed={{
            title: 'Alerts did not load',
            description: 'Check your connection and try again.',
          }}
        >
          {(rows) => (
            <RefreshRegion updating={query.isPlaceholderData}>
              <RowActionsContext value={rowActions}>
                {desktop ? (
                  <DataTable
                    columns={COLUMNS}
                    rows={rows}
                    getRowId={(alert) => String(alert.id)}
                    caption={tab === 'active' ? 'Active alerts' : 'Expired alerts'}
                    rowHeight="lg"
                    bodySize="m"
                    headWeight="regular"
                    // The whole row is the target (mock): the softer wash, the name underlined,
                    // a pointer; no link, so it stays out of the Tab order (the ⋮ is the keyboard's).
                    washRows
                    washTone="soft"
                    rowClassName={() =>
                      'cursor-pointer [&_[data-row-name]]:underline-offset-3 [&:hover_[data-row-name]]:underline'
                    }
                    rowProps={(alert) => ({ onClick: openRow(alert) })}
                  />
                ) : (
                  <AlertCards rows={rows} openRow={openRow} />
                )}
              </RowActionsContext>
            </RefreshRegion>
          )}
        </QueryState>
      )}
    </ScreenLayout>
  )
}
